import {useCallback, useEffect, useRef, useState} from 'react';
import styles from './tabs.less';

interface TabsProps {
    tabs: string[];
    sticky: boolean;
    defaultActivatedIndex?: number;
    onItemClick?: (category: string, index: number) => void;
}

function Tabs(props: TabsProps) {

    const {
        tabs,
        sticky,
        defaultActivatedIndex = 0,
        onItemClick,
    } = props;

    const [isStickey, setIsStickey] = useState(false);
    const [activatedIndex, setActivatedIndex] = useState(defaultActivatedIndex);
    const [tabsHeight, setTabsHeight] = useState(0);
    const tabsRef = useRef<HTMLDivElement>(null);
    const cacheTop = useRef(0);

    const listenerPageScroll = useCallback(
        fn => {
            window.addEventListener('scroll', fn, false);
            return () => window.removeEventListener('scroll', fn, false);
        },
        []
    );

    const initListContainerTop = useCallback(
        () => {
            const top = tabsRef.current?.getBoundingClientRect()?.top || 0;
            const scrollTop = document.documentElement.scrollTop || 0;
            cacheTop.current = top + scrollTop;
        },
        []
    );

    const handleScrollSticky = useCallback(
        () => {
            const offsetTop = tabsRef.current?.getBoundingClientRect()?.top || 0;
            if (offsetTop <= 0) {
                initListContainerTop();
            }
            setIsStickey(offsetTop <= 0);
        },
        [initListContainerTop]
    );

    useEffect(
        () => {
            initListContainerTop();
        },
        [initListContainerTop]
    );

    useEffect(
        () => {
            if (sticky) {
                const offsetTop = tabsRef.current?.getBoundingClientRect()?.height;
                setTabsHeight(offsetTop || 0);
                const removeEvent = listenerPageScroll(handleScrollSticky);
                return removeEvent;
            }
        },
        [handleScrollSticky, listenerPageScroll, sticky]
    );

    const handleClick = useCallback(
        (category: string, index: number) => () => {
            if (isStickey) {
                window.scrollTo({
                    top: cacheTop.current || 0,
                    behavior: 'smooth',
                });
            }
            setActivatedIndex(index);
            onItemClick && onItemClick(category, index);
        },
        [isStickey, onItemClick]
    );

    const renderCateText = useCallback(
        (category: string) => {
            let texts = [category];
            /**
             * 换行时，保证第二行至少两个字
             */
            if (category?.length < 7) {
                texts = [category.slice(0, -2), category.slice(-2)];
            }
            return texts.map(text => <div key={category + text}>{text}</div>);
        },
        []
    );

    return (
        <div ref={tabsRef}>
            <div
                className={styles(styles.tabs, {
                    [styles.fixed]: isStickey,
                })}
            >
                <div className={styles.inner}>
                    {
                        tabs?.map((category, index) => (
                            <div
                                key={category}
                                className={styles(styles.tab, {
                                    activated: activatedIndex === index,
                                })}
                                onClick={handleClick(category, index)}
                            >
                                {renderCateText(category)}
                            </div>
                        ))
                    }
                </div>
            </div>
            <div style={{height: `${isStickey ? tabsHeight : 0}px`}}></div>
        </div>
    );
}

export default Tabs;
